import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  FormControlLabel,
  Switch,
  FormGroup,
  Stack
} from '@mui/material';
import { useParams } from 'react-router-dom';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import '@fortawesome/fontawesome-free/css/all.min.css';
import { simulationService } from '../../services/simulation.service';
import createBladeIcon from '../Install-base/markers/BladeIcon';

const SimulationMap = () => {
  const { runId } = useParams();
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const MAP_LAYERS = {
    STORES: {
      id: 'stores',
      name: 'Stores',
      icon: 'circle',
      color: '#2196f3',
      visible: true,
      renderMarker: (data, map) => {
        if (data.latitude && data.longitude) {
          const marker = L.circleMarker([data.latitude, data.longitude], {
            radius: 6,
            fillColor: '#2196f3',
            color: '#2196f3',
            weight: 2,
            opacity: 1,
            fillOpacity: 0.4
          }).addTo(map);

          marker.bindPopup(`
            <div style="min-width: 200px;">
              <strong>${data.store_name}</strong><br>
              <strong>ID:</strong> ${data.store_id}<br>
              <strong>Total On Hand:</strong> ${data.total_on_hand.toLocaleString()}<br>
              <strong>Total Pipeline:</strong> ${data.total_pipeline.toLocaleString()}<br>
              <strong>Total Target:</strong> ${data.total_target.toLocaleString()}<br>
              <strong>Total Safety Stock:</strong> ${data.total_safety_stock.toLocaleString()}<br>
              <strong>SKU Count:</strong> ${data.sku_count}<br>
              <strong>Stock Status:</strong> ${data.stock_status}<br>
              <br>
              <a href="/simulation/timeline/${runId}/${data.store_id}" style="color: #2196f3; text-decoration: none;">
                View Timeline
              </a>
            </div>
          `);
          return marker;
        }
        return null;
      }
    },
    SUPPLIERS: {
      id: 'suppliers',
      name: 'Suppliers',
      icon: 'warehouse',
      color: '#4caf50',
      visible: true,
      renderMarker: (data, map) => {
        console.log('Rendering supplier marker:', data); // Debug log
        if (data.supplier_warehouse_latitude && data.supplier_warehouse_longitude) {
          const icon = L.divIcon({
            html: `<i class="fas fa-warehouse" style="
              color: #4caf50; 
              font-size: 24px;
              text-shadow: 2px 2px 2px rgba(0,0,0,0.2);
            "></i>`,
            className: 'supplier-marker',
            iconSize: [24, 24],
            iconAnchor: [12, 12]
          });

          const marker = L.marker([
            parseFloat(data.supplier_warehouse_latitude),
            parseFloat(data.supplier_warehouse_longitude)
          ], {
            icon
          }).addTo(map);

          marker.bindPopup(`
            <div style="min-width: 200px;">
              <strong>${data.supplier_name}</strong><br>
              <strong>ID:</strong> ${data.supplier_id}<br>
              <strong>Type:</strong> ${data.supplier_type}<br>
              <strong>Category:</strong> ${data.supplier_category}<br>
              <strong>Location:</strong> ${data.supplier_warehouse_city}, ${data.supplier_warehouse_state}<br>
              <strong>Address:</strong> ${data.supplier_warehouse_location}<br>
              <strong>Contact:</strong> ${data.supplier_contact_phone || 'N/A'}<br>
              <strong>Payment Terms:</strong> ${data.supplier_payment_terms}<br>
              <strong>Discount Rate:</strong> ${data.supplier_discount_rate}%
            </div>
          `);
          return marker;
        }
        console.log('Invalid supplier coordinates:', data); // Debug log
        return null;
      }
    }
  };

  const [layers, setLayers] = useState({
    suppliers: { ...MAP_LAYERS.SUPPLIERS, visible: true },
    stores: { ...MAP_LAYERS.STORES, visible: true }
  });
  const [layerData, setLayerData] = useState({});
  const [mapInitialized, setMapInitialized] = useState(false);

  useEffect(() => {
    if (runId) {
      fetchData();
    }
  }, [runId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch suppliers data
      const suppliersData = await simulationService.getSimulationSuppliers(runId);
      const suppliersResponse = Array.isArray(suppliersData) ? suppliersData : [];

      if (suppliersResponse.length === 0) {
        setError('No supplier data available for this simulation run');
        return;
      }

      // Validate supplier data
      const validSuppliers = suppliersResponse.filter(supplier => {
        const hasValidCoords = supplier.supplier_warehouse_latitude && 
                             supplier.supplier_warehouse_longitude &&
                             !isNaN(parseFloat(supplier.supplier_warehouse_latitude)) &&
                             !isNaN(parseFloat(supplier.supplier_warehouse_longitude));
        return hasValidCoords;
      });

      if (validSuppliers.length === 0) {
        setError('No suppliers with valid coordinates found');
        return;
      }

      // Fetch stores data
      const storesData = await simulationService.getSimulationStores(runId);
      const storesResponse = Array.isArray(storesData) ? storesData : [];

      if (storesResponse.length === 0) {
        setError('No store data available for this simulation run');
        return;
      }

      // Fetch inventory data grouped by store
      let storesWithInventory = storesResponse;
      try {
        const inventoryData = await simulationService.getSimulationInventory(runId, { 
          group_by: 'store',
          limit: 1000 // Add a reasonable limit to prevent timeout
        });
        const inventoryResponse = Array.isArray(inventoryData) ? inventoryData : [];

        // Merge store data with inventory data
        storesWithInventory = storesResponse.map(store => {
          const storeInventory = inventoryResponse.find(inv => inv.store_id === store.store_id) || {};
          return {
            ...store,
            total_on_hand: storeInventory.total_on_hand || 0,
            total_pipeline: storeInventory.total_pipeline || 0,
            total_target: storeInventory.total_target || 0,
            total_safety_stock: storeInventory.total_safety_stock || 0,
            sku_count: storeInventory.total_skus || 0,
            stock_status: storeInventory.stock_status || 'unknown'
          };
        });
      } catch (inventoryError) {
        console.warn('Error fetching inventory data:', inventoryError);
        // Continue with stores data even if inventory fetch fails
        storesWithInventory = storesResponse.map(store => ({
          ...store,
          total_on_hand: 0,
          total_pipeline: 0,
          total_target: 0,
          total_safety_stock: 0,
          sku_count: 0,
          stock_status: 'unknown'
        }));
      }

      // Set layer data
      const newLayerData = {
        suppliers: validSuppliers,
        stores: storesWithInventory
      };
      setLayerData(newLayerData);

      // Initialize map if not already initialized
      if (!mapInitialized) {
        initializeMap();
      }
    } catch (err) {
      console.error('Error fetching simulation data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const initializeMap = () => {
    if (!mapRef.current || mapInstanceRef.current) return;

    try {
      mapInstanceRef.current = L.map(mapRef.current).setView([39.5, -98.35], 4);
      
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© OpenStreetMap contributors'
      }).addTo(mapInstanceRef.current);

      // Create legend
      const legend = L.control({ position: 'bottomright' });
      legend.onAdd = () => {
        const div = L.DomUtil.create('div', 'info legend');
        div.style.backgroundColor = 'white';
        div.style.padding = '10px';
        div.style.border = '2px solid rgba(0,0,0,0.2)';
        div.style.borderRadius = '4px';
        div.innerHTML = Object.values(layers)
          .filter(layer => layer.visible)
          .map(layer => `
            <div style="margin-bottom: 5px;">
              <i class="fas fa-${layer.icon}" style="color: ${layer.color};"></i> ${layer.name}
            </div>
          `).join('');
        return div;
      };
      legend.addTo(mapInstanceRef.current);
      setMapInitialized(true);
    } catch (error) {
      console.error('Error initializing map:', error);
      setError('Failed to initialize map');
    }
  };

  const toggleLayer = (layerId) => {
    setLayers(prev => ({
      ...prev,
      [layerId]: {
        ...prev[layerId],
        visible: !prev[layerId].visible
      }
    }));
  };

  useEffect(() => {
    if (runId) {
      fetchData();
    }
  }, [runId]);

  useEffect(() => {
    if (mapInstanceRef.current && Object.keys(layerData).length > 0) {
      // Clear existing markers
      mapInstanceRef.current.eachLayer((layer) => {
        if (layer instanceof L.Marker || layer instanceof L.CircleMarker) {
          mapInstanceRef.current.removeLayer(layer);
        }
      });

      // Add new markers
      Object.entries(layers).forEach(([layerId, layer]) => {
        if (layer.visible && layerData[layerId]) {
          layerData[layerId].forEach(data => {
            try {
              layer.renderMarker(data, mapInstanceRef.current);
            } catch (error) {
              console.error(`Error adding marker for ${layerId}:`, error);
            }
          });
        }
      });
    }
  }, [layerData, layers]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom>
        Simulation Map - Run ID: {runId}
      </Typography>
      <Stack direction="row" spacing={2} mb={2}>
        <FormGroup>
          {Object.entries(layers).map(([layerId, layer]) => (
            <FormControlLabel
              key={layerId}
              control={
                <Switch
                  checked={layer.visible}
                  onChange={() => toggleLayer(layerId)}
                  color="primary"
                />
              }
              label={layer.name}
            />
          ))}
        </FormGroup>
      </Stack>
      <Paper elevation={3} style={{ height: '600px', position: 'relative' }}>
        <div ref={mapRef} style={{ height: '100%', width: '100%' }} />
      </Paper>
    </Box>
  );
};

export default SimulationMap; 